import React from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  Card,
  CardContent,
  CardTitle,
} from '@rewardops/replit/src/components/ui/card'
import { CardHeader, Spinner } from '@rewardops/ui'

import { useApi } from '~/components/hooks/use-api'
import { ChartType, PAWSectionType } from './paw-section-types'

export const ComparisonCharts: React.FC<PAWSectionType> = ({
  content,
  data,
}: PAWSectionType) => {
  const { api } = useApi()

  const { data: charts, isLoading } = api.pawDashboards.getByRole.useQuery({
    role: data?.role,
    sectionId: 'comparison-charts',
  })

  return (
    <section id={content?.sectionId} data-testid={content?.sectionId}>
      <h2 className="mb-6 text-xl font-semibold">
        {content?.title ?? 'Team Performance'}
      </h2>
      <div className="grid grid-cols-1 gap-6">
        {isLoading ? (
          <Spinner />
        ) : (
          charts?.map((chartData: any, index: number) => (
            <Card key={index}>
              <CardHeader>
                <CardTitle>{chartData?.title ?? 'Points'}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  {chartData?.type === ChartType.Pie ? (
                    <PieChart>
                      <Pie
                        data={chartData?.data}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {chartData?.data.map((entry: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [
                          ((value as number) / 1000).toFixed(0) + 'K',
                          'Points',
                        ]}
                      />
                    </PieChart>
                  ) : (
                    <BarChart data={chartData?.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey={chartData?.dataKey ?? 'name'} />
                      <YAxis
                        label={{
                          value: 'Points (K)',
                          angle: -90,
                          position: 'insideLeft',
                        }}
                      />
                      <Tooltip
                        formatter={(value, name) => [
                          name === 'points' ? `${value}K` : `${value}K`,
                          name === 'points' ? 'Points' : 'Target',
                        ]}
                      />
                      <Legend />
                      <Bar
                        dataKey="points"
                        fill="var(--tenant-primary-color)"
                        name="Points"
                      />
                      <Bar dataKey="target" fill="#94a3b8" name="Target" />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </section>
  )
}
